import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Pagination,
  TextField,
  Button,
  IconButton,
  Skeleton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
  Avatar,
  InputAdornment,
  TableContainer,
  TableHead
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Image as ImageIcon,
  Visibility as ViewIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import Page from 'src/components/Page';
import palette from 'src/theme/palette';
import { getBlogs, deleteBlogs } from 'src/ApiServices';
import { toast } from 'react-toastify';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlogModal from 'src/sections/BlogModal';
import BlogViewModal from 'src/sections/BlogViewModal';

// Import Fancybox
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';

export default function Blog() {
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  });
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Initialize Fancybox
  useEffect(() => {
    Fancybox.bind('[data-fancybox]', {
      Thumbs: false,
      Toolbar: true
    });

    return () => {
      Fancybox.destroy();
    };
  }, []);

  // Fetch blogs data function
  const fetchBlogsData = async (currentPage, search = '') => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 10
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      const response = await getBlogs(params);
      setBlogs(response.data || []);

      if (response.pagination) {
        setPagination(response.pagination);
      } else {
        setPagination({
          current_page: currentPage,
          per_page: 10,
          total: response.data?.length || 0,
          last_page: Math.ceil((response.data?.length || 0) / 10) || 1
        });
      }
    } catch (error) {
      console.error('Failed to load blogs', error);
      toast.error('Failed to load blogs');
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Search handlers
  const handleSearchSubmit = useCallback(async () => {
    setPage(1);
    await fetchBlogsData(1, searchQuery);
  }, [searchQuery]);

  const handleClearSearch = useCallback(async () => {
    setSearchQuery('');
    setPage(1);
    await fetchBlogsData(1, '');
  }, []);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleAddClick = () => {
    setSelectedBlog(null);
    setModalOpen(true);
  };

  const handleEditClick = (blog) => {
    setSelectedBlog(blog);
    setModalOpen(true);
  };

  const handleViewClick = (blog) => {
    setSelectedBlog(blog);
    setViewModalOpen(true);
  };

  const handleDeleteClick = (blog) => {
    setSelectedBlog(blog);
    setDeleteModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setViewModalOpen(false);
    setDeleteModalOpen(false);
    setSelectedBlog(null);
  };

  const handleModalSuccess = () => {
    handleCloseModal();
    fetchBlogsData(page, searchQuery);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedBlog) return;

    try {
      setDeleteLoading(true);
      await deleteBlogs(selectedBlog.id);
      toast.success('Blog deleted successfully!');

      // Check if we need to go to previous page after deletion
      if (blogs.length === 1 && page > 1) {
        const newPage = page - 1;
        setPage(newPage);
        await fetchBlogsData(newPage, searchQuery);
      } else {
        await fetchBlogsData(page, searchQuery);
      }

      setDeleteModalOpen(false);
      setSelectedBlog(null);
    } catch (error) {
      console.error('Failed to delete blog:', error);
      toast.error(error.response?.data?.message || 'Failed to delete blog!');
    } finally {
      setDeleteLoading(false);
    }
  };
  const truncateWords = (text, wordLimit = 5) => {
    if (!text) return 'N/A';
    const words = text.split(' ');
    return words.length > wordLimit ? words.slice(0, wordLimit).join(' ') + '...' : text;
  };

  // Load blogs when page changes
  useEffect(() => {
    fetchBlogsData(page, searchQuery);
  }, [page]);

  // Load initial data
  useEffect(() => {
    fetchBlogsData(1, '');
  }, []);

  const renderImageAvatar = (blog) => {
    if (blog.background_image) {
      return (
        <Tooltip title="Click to view image">
          <Avatar
            src={blog.background_image}
            sx={{
              width: 60,
              height: 60,
              cursor: 'pointer',
              border: `2px solid ${palette.primary.main}`,
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                borderColor: palette.primary.dark,
                boxShadow: `0 4px 12px ${palette.primary.main}40`
              }
            }}
            data-fancybox="blog-gallery"
            data-src={blog.background_image}
            data-caption={blog.title || 'Blog Image'}
          />
        </Tooltip>
      );
    } else {
      return (
        <Avatar
          sx={{
            width: 60,
            height: 60,
            bgcolor: palette.grey[300],
            borderRadius: 2
          }}
        >
          <ImageIcon sx={{ color: palette.grey[600] }} />
        </Avatar>
      );
    }
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const stickyImageColumnStyle = {
    px: 2,
    position: { xs: 'static', lg: 'sticky' },
    left: 0,
    zIndex: 1,
    backgroundColor: theme.palette.background.paper,
    minWidth: 80,
    width: 80,
    textAlign: 'center'
  };

  const stickyTitleColumnStyle = {
    px: 2,
    position: { xs: 'static', lg: 'sticky' },
    left: 80,
    zIndex: 1,
    backgroundColor: theme.palette.background.paper,
    minWidth: 200
  };

  const stickyActionsColumnStyle = {
    px: 2,
    position: { xs: 'static', lg: 'sticky' },
    right: 0,
    width: 140,
    textAlign: 'center',
    zIndex: 1,
    backgroundColor: theme.palette.background.paper
  };

  const renderSkeletonRows = () => {
    return Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        <TableCell sx={stickyImageColumnStyle}>
          <Skeleton variant="rectangular" width={60} height={60} sx={{ borderRadius: 2 }} />
        </TableCell>
        <TableCell sx={stickyTitleColumnStyle}>
          <Skeleton variant="text" />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" />
        </TableCell>
        <TableCell sx={stickyActionsColumnStyle}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="circular" width={32} height={32} />
          </Box>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Page title={`Blog | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            mt: 2,
            pb: 2,
            pl: 1
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 25 },
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark
            }}
          >
            Manage Blog
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              background: palette.primary.main,
              color: palette.primary.contrastText,
              height: 40,
              '&:hover': { background: palette.primary.dark }
            }}
          >
            Add Blog
          </Button>
        </Box>

        {/* Search Section */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={10}>
            <TextField
              variant="outlined"
              placeholder="Search blogs..."
              size="medium"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: palette.text.secondary }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                backgroundColor: theme.palette.background.paper,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': { borderColor: theme.palette.primary.main },
                  '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearchSubmit}
                fullWidth
                sx={{
                  height: 56,
                  fontSize: '14px',
                  fontFamily: 'poppinsMedium'
                }}
                startIcon={<SearchIcon />}
              >
                Search
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Blog Table */}
        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer
              sx={{
                overflow: 'auto',
                '&::-webkit-scrollbar': { height: 8, width: 8 },
                '&::-webkit-scrollbar-track': { background: palette.grey[200] },
                '&::-webkit-scrollbar-thumb': {
                  background: palette.grey[400],
                  borderRadius: 2
                }
              }}
            >
              <Table
                sx={{
                  minWidth: 1000,
                  '& .MuiTableCell-head': {
                    backgroundColor: theme.palette.background.paper,
                    color: palette.primary.tableHeadTitle,
                    fontFamily: 'poppinsSemiBold',
                    fontSize: '14px',
                    borderBottom: `2px solid ${palette.divider}`,
                    py: 2
                  },
                  '& .MuiTableCell-body': {
                    color: palette.text.primary,
                    fontFamily: 'poppinsRegular',
                    fontSize: '14px',
                    borderBottom: `1px solid ${palette.divider}`,
                    py: 2
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...stickyImageColumnStyle, zIndex: 2 }}>Image</TableCell>
                    <TableCell sx={{ ...stickyTitleColumnStyle, zIndex: 2 }}>Title</TableCell>
                    <TableCell sx={{ px: 2 }}>Created At</TableCell>
                    {/* <TableCell sx={{ px: 2 }}>Meta Title</TableCell> */}
                    {/* <TableCell sx={{ px: 2 }}>Meta Description</TableCell>
                    <TableCell sx={{ px: 2 }}>Slug</TableCell> */}
                    <TableCell sx={{ ...stickyActionsColumnStyle, zIndex: 2 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    renderSkeletonRows()
                  ) : blogs.length > 0 ? (
                    blogs.map((blog) => (
                      <TableRow
                        key={blog.id}
                        sx={{
                          '&:hover': { backgroundColor: palette.action.hover },
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <TableCell sx={stickyImageColumnStyle}>{renderImageAvatar(blog)}</TableCell>
                        <TableCell sx={stickyTitleColumnStyle}>
                          {truncateWords(blog?.title, 5)}
                        </TableCell>

                        {/* <TableCell sx={{ px: 2 }}>
                          <Typography
                            sx={{
                              maxWidth: 150,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {truncateText(blog.meta_title)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ px: 2 }}>
                          <Typography
                            sx={{
                              maxWidth: 150,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {truncateText(blog.meta_description)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ px: 2 }}>
                          <Typography
                            sx={{
                              maxWidth: 150,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: palette.text.secondary,
                              fontSize: '13px'
                            }}
                          >
                            {blog.slug || 'N/A'}
                          </Typography>
                        </TableCell> */}

                        <TableCell sx={{ px: 2 }}>
                          <Typography
                            sx={{
                              maxWidth: 150,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: palette.text.secondary,
                              fontSize: '13px'
                            }}
                          >
                            {blog.created_at || 'N/A'}
                          </Typography>
                        </TableCell>

                        <TableCell sx={stickyActionsColumnStyle}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                            <Tooltip title="View Blog">
                              <IconButton
                                size="small"
                                onClick={() => handleViewClick(blog)}
                                sx={{ color: palette.info.main }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Blog">
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(blog)}
                                sx={{ color: palette.primary.main }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Blog">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(blog)}
                                sx={{ color: palette.error.main }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">
                          {searchQuery ? 'No blogs found matching your search' : 'No blogs found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {!loading && pagination.total >= 0 && pagination.last_page >= 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {/* Add/Edit Modal */}
        <BlogModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSuccess={handleModalSuccess}
          blog={selectedBlog}
        />

        {/* View Modal */}
        <BlogViewModal open={viewModalOpen} onClose={handleCloseModal} blog={selectedBlog} />

        {/* Delete Confirmation Modal */}
        <Dialog
          open={deleteModalOpen}
          onClose={handleCloseModal}
          PaperProps={{
            sx: {
              borderRadius: 2,
              padding: 1,
              margin: { xs: 2, sm: 3 },
              maxWidth: '400px',
              width: '100%'
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', py: 3, pb: 2 }}>
            <WarningAmberIcon
              sx={{
                fontSize: 60,
                color: palette.warning.main,
                mb: 2
              }}
            />
            <Typography
              variant="h6"
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary }}
            >
              Confirm Deletion
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <DialogContentText sx={{ color: palette.text.secondary, fontFamily: 'poppinsRegular' }}>
              Are you sure you want to delete blog <strong>"{selectedBlog?.title}"</strong>?
              <br />
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3 }}>
            <Button
              onClick={handleCloseModal}
              variant="outlined"
              sx={{
                borderColor: palette.grey[400],
                color: palette.text.primary,
                minWidth: 100
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: palette.error.main,
                color: palette.error.contrastText,
                minWidth: 100,
                '&:hover': {
                  backgroundColor: palette.error.dark
                }
              }}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
}
